<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import SelectField from './SelectField.vue'
import AddUserButtonAndModal from '../../Entity/AddUserButtonAndModal.vue'
import { useFormFieldDef } from '../../../../../Composables/useFormFieldDef.js'
import { useFormField } from '../../../../../Composables/useFormField.ts'
import { getOptionValue } from '../../../../../Utils/selectOptionUtils'
import { renderSelectionTemplate } from '../../../../../Utils/selectUtils'
import useUser from '../../../../../Composables/User/useUser.ts'

const props = defineProps({
  name: { type: String, required: true },
  domain: { type: String, default: null },
  formData: { type: Object, default: null },
  formRef: { type: String, default: '' },
  endpoint: { type: String, default: null },
  financeId: { type: Number, default: null },
  objectId: { type: Number, default: null },
  clientId: { type: [String, Number], default: null },
  entityId: { type: [String, Number], default: null },
  simulationId: { type: [String, Number], default: null },
  contractId: { type: [String, Number], default: null },
  userId: { type: [String, Number], default: null },
  addUser: { type: Boolean, default: true },
  addUserPlaceholder: { type: Boolean, default: false },
  user: { type: Object, default: null },
  multiple: { type: Boolean, default: false },
  labelWrapperClasses: { type: String, default: null },
  inputWrapperClasses: { type: String, default: null },
  userEntity: { type: Boolean, default: false },
})

const emit = defineEmits(['addselect'])

const { isDisabled } = useFormFieldDef(props.formData, props.name)
const { value: formValue } = useFormField(props.formData, props.name)
const { isReadOnly } = useUser()

const userToEdit = ref(null)
const internalChoices = ref([])
const selectedUsers = ref([])
const selectedUser = ref(null)
const editModalRef = ref(null)
const multiSelectRef = ref(null)

const wrapperId = computed(() => `ums-${(props.name || '').replace(/\./g, '-')}-${props.formRef || ''}`)
const openAddUserEventName = computed(() => `openAddUserModal__${wrapperId.value}`)
const showAddUserButton = computed(() => props.addUser && !isDisabled.value && !isReadOnly.value)

function deriveChoices() {
  const def = props.formData?.def?.[props.name]
  return def?.options?.choices || []
}

function syncSelectedFromForm() {
  const target = props.formData?.datas?.[props.name] ?? props.formData?.[props.name] ?? null
  if (props.multiple) {
    syncSelectedUsers(target)
  } else {
    syncSelectedUser(target)
  }
}

function syncSelectedUsers(value) {
  if (!value) {
    selectedUsers.value = []
    return
  }

  if (Array.isArray(value)) {
    if (typeof value[0] === 'object') {
      selectedUsers.value = value
    } else {
      selectedUsers.value = internalChoices.value.filter((opt) =>
        value.map(String).includes(String(getOptionValue(opt))),
      )
    }
    return
  }

  const found = internalChoices.value.find((opt) => String(getOptionValue(opt)) === String(value))
  selectedUsers.value = found ? [found] : [{ id: value }]
}

function syncSelectedUser(value) {
  if (!value) {
    selectedUser.value = null
    return
  }

  const found = internalChoices.value.find((opt) => String(getOptionValue(opt)) === String(value))
  selectedUser.value = found || { id: value }
}

function onAddSelect(payload) {
  if (payload?.data) {
    const { user, id } = payload.data
    const newOption = {
      id: props.userEntity ? id : user.id,
      userId: user.id,
      userEntityId: id,
      name: user.name,
      text: user.name,
      user: user,
    }
    internalChoices.value.push(newOption)
    if (props.multiple) {
      selectedUsers.value.push(newOption)
      formValue.value = selectedUsers.value.map((u) => parseInt(getOptionValue(u)))
    } else {
      selectedUser.value = newOption
      formValue.value = parseInt(getOptionValue(newOption))
    }
  }
  emit('addselect', payload)
}

function onUpdateUser(user) {
  internalChoices.value = internalChoices.value.map((opt) => {
    if (String(opt.id) === userToEdit.value.id) {
      return { ...opt, name: user.name, text: user.name, user }
    }
    return opt
  })
  const selectInput = multiSelectRef.value?.selectElement
  if (selectInput) {
    const option = selectInput.querySelector(`option[value="${userToEdit.value.id}"]`)
    if (option) {
      option.textContent = user.name
    }
  }

  nextTick(() => {
    multiSelectRef.value?.refreshDisplay()
  })

  userToEdit.value = null
}

function templateSelectionRender(data) {
  if (!props.multiple) {
    return data.text
  }

  return renderSelectionTemplate(data, function (data) {
    const nodes = []

    nodes.push(document.createTextNode((data.text || '') + ' '))

    const edit = document.createElement('i')
    edit.className = 'cursor-pointer fas fa-pencil-alt'
    edit.addEventListener('click', function (ev) {
      ev.stopPropagation()
      const fullOption = internalChoices.value.find((opt) => String(getOptionValue(opt)) === String(data.id))
      window.dispatchEvent(
        new CustomEvent(openAddUserEventName, {
          detail: {
            optionId: data.id,
            userId: fullOption?.userId || fullOption?.user?.id,
            userEntityId: fullOption?.userEntityId || fullOption?.id,
          },
        }),
      )
    })
    nodes.push(edit)

    return nodes
  })
}

async function openAddUserHandler(e) {
  try {
    userToEdit.value = {
      id: parseInt(e.detail.optionId),
      userId: parseInt(e.detail.userId),
      userEntityId: parseInt(e.detail.userEntityId),
    }

    await nextTick()
    editModalRef.value?.editModal(false)
  } catch {}
}

onMounted(function () {
  const choices = deriveChoices()
  internalChoices.value = Array.isArray(choices) ? [...choices] : []
  syncSelectedFromForm()

  window.addEventListener(openAddUserEventName, openAddUserHandler)
})

onBeforeUnmount(function () {
  window.removeEventListener(openAddUserEventName, openAddUserHandler)
})

watch(
  function () {
    return internalChoices.value
  },
  function () {
    syncSelectedFromForm()
  },
  { deep: true },
)

watch(
  function () {
    return props.formData && props.formData.datas ? props.formData.datas[props.name] : null
  },
  function (nv) {
    if (props.multiple) {
      syncSelectedUsers(nv)
    } else {
      syncSelectedUser(nv)
    }
  },
)
</script>

<template>
  <div>
    <SelectField
      ref="multiSelectRef"
      v-bind="$attrs"
      :name="name"
      :domain="domain"
      :form-data="formData"
      :form-ref="formRef"
      :template-selection-render="templateSelectionRender"
      :choices="internalChoices"
      :multiple="multiple"
      :label-wrapper-classes="labelWrapperClasses"
      :input-wrapper-classes="inputWrapperClasses"
    >
      <template v-if="showAddUserButton" #end>
        <div class="ml-2">
          <AddUserButtonAndModal
            :id="`ums-${name}-add`"
            :user-input-id="name.replace(/\./g, '-') + formRef"
            :endpoint="endpoint"
            :finance-id="financeId"
            :object-id="objectId"
            :client-id="clientId"
            :entity-id="entityId"
            :simulation-id="simulationId"
            :contract-id="contractId"
            :user-entity-id="null"
            :user-entity-active="false"
            :edit="!props.multiple"
            :is-placeholder="addUserPlaceholder"
            @addselect="onAddSelect"
            @update:user="onUpdateUser"
          />
        </div>
      </template>
    </SelectField>

    <AddUserButtonAndModal
      v-if="userToEdit"
      :id="`ums-${userToEdit.id}-edit`"
      ref="editModalRef"
      :key="userToEdit.id"
      :user-input-id="name.replace(/\./g, '-') + formRef"
      :endpoint="endpoint"
      :finance-id="financeId"
      :object-id="objectId"
      :client-id="clientId"
      :entity-id="entityId"
      :simulation-id="simulationId"
      :contract-id="contractId"
      :user-id="userToEdit.userId"
      :user-entity-id="userToEdit.id"
      :user-entity-active="false"
      :add="false"
      :edit="false"
      :is-placeholder="addUserPlaceholder"
      @update:user="onUpdateUser"
    />
  </div>
</template>
